import { rule } from '../../domain/badge-composition-rule.js';

/**
 * @param {BadgeComposition} composition
 * @returns {BadgeCompositionRule[]}
 */
export const positionRules = composition => {
  const { check, variantIs, positionLocationVIs, positionLocationHIs, featureIsEnabled, planIsAtLeast, platformIs } = composition.checkers;

  // global prerequisites for all rules
  if (!variantIs('mid').apply(composition)) {
    return [];
  }

  return [
    // default = middle + right
    rule('position/default')
      .set(() => ({
        aspect: {
          position: {
            specs: {
              vertical: {
                reference: 'top',
                distance: 50,
                unit: '%',
                realign: true
              },
              horizontal: {
                reference: 'right',
                distance: 0,
                unit: 'px'
              }
            }
          }
        },
        parts: {
          sealAsButton: {
            style: {
              justifyContent: 'flex-end'
            }
          }
        }
      })),

    rule('position/default/horizontal/15px')
      .when(platformIs('mobile').or(planIsAtLeast('start'))) // avoid 10px for free/desktop as that's a special case where the badge is connected to the edge
      .set(() => ({
        aspect: {
          position: {
            specs: {
              horizontal: {
                distance: 15,
                unit: 'px'
              }
            }
          }
        },
      })),

    rule('position/vertical/top')
      .when((positionLocationVIs('top')))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              vertical: {
                distance: 32,
                unit: 'px',
                reference: 'top',
                realign: false,
              }
            }
          }
        }
      })),

    rule('position/vertical/bottom')
      .when((positionLocationVIs('bottom')))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              vertical: {
                distance: 12,
                unit: 'px',
                reference: 'bottom',
                realign: false,
              }
            }
          }
        }
      })),

    rule('position/vertical/2p3')
      .when(positionLocationVIs('two_thirds').or(positionLocationVIs('two-thirds')))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              vertical: {
                distance: 66,
                unit: '%',
              }
            }
          }
        }
      })),

    rule('position/vertical/custom')
      .when(positionLocationVIs('custom').and(check(() => composition.aspect.position.vertical.settings)))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              vertical: {
                reference: composition.aspect.position.vertical.settings.reference,
                distance: composition.aspect.position.vertical.settings.distance,
                unit: composition.aspect.position.vertical.settings.unit,
                realign: false,
              }
            }
          }
        }
      })),

    rule('position/horizontal/left')
      .when(positionLocationHIs('left'))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              horizontal: {
                reference: 'left',
              }
            }
          }
        },
        parts: {
          sealAsButton: {
            style: {
              justifyContent: 'flex-start'
            }
          }
        }
      })),

    // horizontal custom position
    rule('position/horizontal/custom')
      .when(positionLocationHIs('custom'))
      .set(() => ({
        aspect: {
          position: {
            specs: {
              horizontal: {
                reference: composition.aspect.position.horizontal.settings.reference,
                distance: composition.aspect.position.horizontal.settings.distance,
                unit: composition.aspect.position.horizontal.settings.unit,
                realign: false,
              }
            }
          }
        }
      })),

    rule('computed/aspect/style')
      .set(() => ({
        aspect: {
          style: {
            [composition.aspect.position.specs.horizontal.reference]: `${composition.aspect.position.specs.horizontal.distance}${composition.aspect.position.specs.horizontal.unit}`, // set left/right
            [composition.aspect.position.specs.vertical.reference]: `${composition.aspect.position.specs.vertical.distance}${composition.aspect.position.specs.vertical.unit}`, // set top/bottom
            'transform': composition.aspect.position.specs.vertical.realign ? `translate(0,-50%)` : null // re-align for middle cases
          }
        }
      })),
  ];
};
